import React from 'react';
import { Button as RBButton } from 'react-bootstrap';
import styled, { css } from 'styled-components';

export type Emphasis = 'primary' | 'secondary' | 'custom';
export type Density = 'small' | 'medium' | 'large';
export type ContentType = 'text' | 'icon' | 'image';

type RBButtonLiteProps = {
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  href?: string;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
};

export interface GovbrSignInProps extends Omit<RBButtonLiteProps, never> {
  emphasis?: Emphasis;
  density?: Density;
  block?: boolean;
  circle?: boolean;
  inverted?: boolean;
  loading?: boolean;
  contentType?: ContentType;
  labelPrefix?: string;
  providerLabel?: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
  imageSrc?: string;
  imageAlt?: string;
  loginUrl?: string;
}

const BaseButton = React.forwardRef<HTMLButtonElement, RBButtonLiteProps>(
  function BaseButton(props, ref) {
    return <RBButton ref={ref as any} {...(props as any)} />;
  },
);

const sizes = {
  small: 32,
  medium: 40,
  large: 48,
} as const;

const StyledButton = styled(BaseButton)<{
  $emphasis: Emphasis;
  $density: Density;
  $block?: boolean;
  $circle?: boolean;
  $inverted?: boolean;
  $loading?: boolean;
}>`
  --button-size: ${(p) => sizes[p.$density]}px;
  --sign-in-img: 20px;
  border-radius: 10em;
  height: var(--button-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border: none !important;
  outline: none !important;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  font-weight: 600;

  ${(p) =>
    p.$block &&
    css`
      width: 100%;
      display: flex;
    `}

  ${(p) =>
    p.$circle &&
    css`
      border-radius: 20%;
      width: var(--button-size);
      padding: 0;
      justify-content: center;
    `}

  ${(p) => {
    if (p.$emphasis === 'custom') {
      return css`
        background-color: #f5f5f5;
        color: #1351b4;
        &:hover {
          background-color: rgba(19, 81, 180, 0.2);
          color: #1351b4;
        }
        &:active {
          background-color: rgba(19, 81, 180, 0.7);
          color: #1351b4;
        }
      `;
    }
    if (p.$emphasis === 'primary') {
      return css`
        background-color: ${p.$inverted ? '#0B1F4D' : '#E0F0FF'};
        color: ${p.$inverted ? '#E0F0FF' : '#111'};
        &:hover {
          background-color: rgba(19, 81, 180, 0.5);
          color: #1351b4;
        }
        &:active {
          background-color: rgba(19, 81, 180, 0.7);
          color: #1351b4;
        }
      `;
    }
    return css`
      background-color: ${p.$inverted ? '#111' : '#F8F8F8'};
      color: ${p.$inverted ? '#E0F0FF' : '#1351b4'};
      &:hover {
        background-color: rgba(19, 81, 180, 0.5);
        color: #1351b4;
      }
      &:active {
        background-color: rgba(19, 81, 180, 0.7);
        color: #1351b4;
      }
    `;
  }}

  ${(p) =>
    p.$loading &&
    css`
      color: transparent !important;
      cursor: progress;
      &::before {
        content: '';
        width: 16px;
        height: 16px;
        border-radius: 20%;
        border: 2px solid currentColor;
        border-color: ${p.$emphasis === 'primary'
            ? p.$inverted
              ? '#0B1F4D'
              : '#E0F0FF'
            : 'currentColor'}
          currentColor transparent;
        display: inline-block;
        animation: spin 0.9s linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `}

  &:not(:disabled) {
    cursor: pointer;
  }

  img {
    max-height: var(--sign-in-img);
  }
`;

const GovbrMark = styled.span`
  font-weight: 700;
  color: #1351b4;
`;

export const GovbrSignInButton: React.FC<GovbrSignInProps> = ({
  emphasis = 'primary',
  density = 'medium',
  block,
  circle,
  inverted,
  loading,
  contentType = 'text',
  labelPrefix = 'Entrar com',
  providerLabel = 'gov.br',
  icon,
  ariaLabel,
  imageSrc,
  imageAlt,
  loginUrl,
  onClick,
  disabled,
  className,
  type = 'button',
  href,
}) => {
  if (contentType === 'icon' && !ariaLabel) {
    console.warn(
      'GovbrSignInButton: ariaLabel é obrigatório quando contentType="icon".',
    );
  }

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (disabled || loading) return;
    if (onClick) return onClick(e);
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  const renderContent = () => {
    switch (contentType) {
      case 'icon':
        return (
          <span aria-hidden={ariaLabel ? true : undefined}>
            {icon ?? <i className="fas fa-user" aria-hidden="true" />}
          </span>
        );
      case 'image':
        return (
          <>
            <span>Entrar com</span>
            {imageSrc && <img src={imageSrc} alt={imageAlt || 'provedor'} />}
          </>
        );
      case 'text':
      default:
        return (
          <>
            <span>{labelPrefix}</span>
            <GovbrMark>{providerLabel}</GovbrMark>
          </>
        );
    }
  };

  return (
    <StyledButton
      className={`br-button sign-in ${emphasis} ${className ?? ''}`}
      $emphasis={emphasis}
      $density={density}
      $block={block}
      $circle={circle}
      $inverted={inverted}
      $loading={loading}
      type={type}
      href={href}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-label={contentType === 'icon' ? ariaLabel : undefined}
    >
      {renderContent()}
    </StyledButton>
  );
};

export default GovbrSignInButton;
//eslint-disable-next-line
